import { matchesListSearch } from '../../lib/list-search.js';
import { Eye as CardIconEye, History as CardIconHistory } from 'lucide-react';
import { SegmentedControl, CardHeader as ContentCardHeader } from '@hue/design-system';
import {
  questionsOf,
  richTextToPlain,
  type HealthTheme,
  type RecordAnswers,
  type RecordBlock,
  type RecordTemplateKind,
} from '@hue/contracts';
import {
  Button,
  ChoiceChips,
  FormErrorSummary,
  IconButton,
  Input,
  Modal,
  StatusPill,
  StatusText,
  ToggleChips,
  useSnackbar,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, GripVertical, FileText, PencilLine, Plus, Trash2 } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import {
  getApiError,
  getApiIssues,
  recordsApi,
  type RecordServiceOptions,
  type RecordTemplateDetail,
  type RecordTemplateSettings,
  type RecordTemplateSummary,
} from '../../lib/api.js';
import { formatDate } from '../../lib/time-zone.js';
import {
  ListFilterBar,
  EmptyCard,
  ErrorCard,
  LoadingCard,
  RequestAlert,
} from '../../app/data-view.js';
import { BlockEditor } from './block-editor.js';
import { RecordForm } from './record-form.js';
import { Notice, ThemeTag, Toggle } from './shared.js';
import { blockKinds, cn, newBlock, questionTypes, themeOf } from './helpers.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';

export interface RecordTemplatesView {
  template?: string | undefined;
  mode: 'edit' | 'preview';
}

const listKey = ['company', 'records', 'templates'] as const;
const detailKey = (id: string) => ['company', 'records', 'templates', id] as const;

/// Modelos de ficha (ADR 0038, decisões 3 e 4): quem monta escolhe os blocos, e quem atende só vê a
/// mudança ao publicar. Nome, serviços e assinatura valem na hora; cada bloco aplicado vai para o rascunho.
export function RecordTemplates({
  timeZone,
  today,
  view,
  onView,
  onOpenThemes,
}: {
  timeZone: string;
  today: string;
  view: RecordTemplatesView;
  onView: (patch: Partial<RecordTemplatesView>) => void;
  onOpenThemes: () => void;
}) {
  const templates = useQuery({
    queryKey: listKey,
    queryFn: recordsApi.listTemplates,
    retry: false,
  });
  const services = useQuery({
    queryKey: ['company', 'records', 'services'],
    queryFn: recordsApi.listServices,
    retry: false,
  });
  const themes = useQuery({
    queryKey: ['company', 'records', 'themes'],
    queryFn: recordsApi.listThemes,
    retry: false,
  });
  const [creating, setCreating] = useState(false);
  const selected = view.template ?? templates.data?.[0]?.id;

  return (
    <section aria-labelledby="record-templates-title">
      <PageHeader
        trail="Configurações / Modelos de ficha"
        id="record-templates-title"
        title="Modelos de ficha"
        description={
          <>
            Cada serviço tem a sua ficha. Blocos e perguntas só mudam para quem atende ao publicar;
            fichas já gravadas continuam com as perguntas que responderam.
          </>
        }
        actions={
          <>
            <CreateButton onClick={() => setCreating(true)}>Novo modelo</CreateButton>
          </>
        }
      />
      <div className="grid gap-6">
        {templates.isLoading || themes.isLoading || services.isLoading ? (
          <LoadingCard label="Carregando modelos" />
        ) : templates.error || themes.error || services.error ? (
          <ErrorCard message={getApiError(templates.error ?? themes.error ?? services.error)} />
        ) : !templates.data!.length ? (
          <EmptyCard
            icon={FileText}
            title="Nenhum modelo de ficha"
            description="Crie o primeiro modelo, monte os blocos e publique para ele aparecer no prontuário das clientes."
          />
        ) : (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,17rem)_minmax(0,1fr)] lg:items-start">
            <TemplateList
              templates={templates.data!}
              services={services.data!}
              selected={selected}
              onSelect={(template) => onView({ template })}
            />
            {selected ? (
              <TemplateWorkspace
                key={selected}
                templateId={selected}
                services={services.data!}
                themes={themes.data!}
                timeZone={timeZone}
                today={today}
                mode={view.mode}
                onMode={(mode) => onView({ mode })}
                onOpenThemes={onOpenThemes}
              />
            ) : null}
          </div>
        )}

        {creating ? (
          <SettingsDialog
            services={services.data}
            onClose={() => setCreating(false)}
            onCreated={(template) => {
              setCreating(false);
              onView({ template: template.id, mode: 'edit' });
            }}
          />
        ) : null}
      </div>
    </section>
  );
}

function servicesLabel(template: RecordTemplateSummary, services: RecordServiceOptions): string {
  if (template.allServices || !services.available) return 'todos os serviços';
  return template.serviceIds
    .map(
      (id) =>
        services.services.find((service) => service.id === id)?.displayName ?? 'serviço removido',
    )
    .join(', ');
}

/// A ordem dos modelos vale para a empresa inteira — é a das fichas no prontuário do cliente. A
/// alfabética é o padrão; mover um modelo passa para a manual, que vale até alguém aplicar a
/// alfabética de novo.
function TemplateList({
  templates,
  services,
  selected,
  onSelect,
}: {
  templates: RecordTemplateSummary[];
  services: RecordServiceOptions;
  selected: string | undefined;
  onSelect: (templateId: string) => void;
}) {
  const [search, setSearch] = useState('');
  const visible = templates.filter((template) =>
    matchesListSearch(`${template.displayName} ${servicesLabel(template, services)}`, search),
  );
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const manual = templates.some((template) => template.position !== null);

  async function reorder(templateIds: string[] | null, message: string) {
    setBusy(true);
    setError(undefined);
    try {
      queryClient.setQueryData(listKey, await recordsApi.reorderTemplates(templateIds));
      await queryClient.invalidateQueries({ queryKey: ['company', 'records', 'chart'] });
      snackbar.success(message);
    } catch (failure) {
      setError(getApiError(failure));
    } finally {
      setBusy(false);
    }
  }

  function move(index: number, delta: number) {
    const ids = templates.map((template) => template.id);
    const [item] = ids.splice(index, 1);
    ids.splice(index + delta, 0, item!);
    void reorder(ids, 'Ordem dos modelos atualizada.');
  }

  return (
    <nav
      aria-label="Modelos"
      className="grid gap-1 rounded-2xl border border-surface-border bg-surface-card p-2 shadow-soft"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-2 pb-1 pt-1">
        <span className="text-xs text-content-muted">
          {manual ? 'Ordem manual' : 'Ordem alfabética'}
        </span>
        {manual ? (
          <button
            type="button"
            disabled={busy}
            onClick={() => void reorder(null, 'Modelos em ordem alfabética.')}
            className="cursor-pointer text-xs font-semibold text-brand-400 hover:underline disabled:cursor-wait"
          >
            Aplicar ordem alfabética
          </button>
        ) : null}
      </div>
      <ListFilterBar
        variant="panel"
        searchLabel="Buscar modelos"
        search={search}
        onSearchChange={setSearch}
        count={`${visible.length} ${visible.length === 1 ? 'modelo' : 'modelos'}`}
      />
      {error ? <RequestAlert message={error} /> : null}
      {templates.map((template, index) =>
        matchesListSearch(
          `${template.displayName} ${servicesLabel(template, services)}`,
          search,
        ) ? (
          <div key={template.id} className="flex items-start gap-1">
            <TemplateItem
              template={template}
              services={services}
              selected={template.id === selected}
              onSelect={() => onSelect(template.id)}
            />
            {templates.length > 1 ? (
              <div className="grid shrink-0 pt-1">
                <IconButton
                  label={`Subir ${template.displayName}`}
                  icon={<ArrowUp className="size-4" />}
                  disabled={index === 0 || busy}
                  onClick={() => move(index, -1)}
                />
                <IconButton
                  label={`Descer ${template.displayName}`}
                  icon={<ArrowDown className="size-4" />}
                  disabled={index === templates.length - 1 || busy}
                  onClick={() => move(index, 1)}
                />
              </div>
            ) : null}
          </div>
        ) : null,
      )}
    </nav>
  );
}

function TemplateItem({
  template,
  services,
  selected,
  onSelect,
}: {
  template: RecordTemplateSummary;
  services: RecordServiceOptions;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-current={selected ? 'page' : undefined}
      onClick={onSelect}
      className={cn(
        'grid min-w-0 flex-1 cursor-pointer gap-1 rounded-xl px-3 py-2.5 text-left transition',
        selected ? 'bg-brand-500/12 ring-1 ring-brand-500/40' : 'hover:bg-content/5',
      )}
    >
      <span className="text-sm font-semibold">{template.displayName}</span>
      <span className="text-xs text-content-muted">
        Vale para {servicesLabel(template, services)}
      </span>
      <span className="flex flex-wrap gap-1.5">
        {template.publishedVersion ? (
          <StatusPill tone="positive">v{template.publishedVersion} em uso</StatusPill>
        ) : (
          <StatusPill tone="neutral">Nunca publicado</StatusPill>
        )}
        {template.hasDraft ? <StatusPill tone="warning">Rascunho</StatusPill> : null}
        {template.kind === 'CHECK' ? <StatusPill tone="neutral">Checagem</StatusPill> : null}
        {template.active ? null : <StatusPill tone="neutral">Arquivado</StatusPill>}
      </span>
    </button>
  );
}

function canonical(value: unknown): string {
  return JSON.stringify(value, (_key, item: unknown) =>
    item && typeof item === 'object' && !Array.isArray(item)
      ? Object.fromEntries(
          Object.entries(item).sort(([left], [right]) => left.localeCompare(right)),
        )
      : item,
  );
}

function TemplateWorkspace({
  templateId,
  services,
  themes,
  timeZone,
  today,
  mode,
  onMode,
  onOpenThemes,
}: {
  templateId: string;
  services: RecordServiceOptions;
  themes: HealthTheme[];
  timeZone: string;
  today: string;
  mode: 'edit' | 'preview';
  onMode: (mode: 'edit' | 'preview') => void;
  onOpenThemes: () => void;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const detail = useQuery({
    queryKey: detailKey(templateId),
    queryFn: () => recordsApi.getTemplate(templateId),
    retry: false,
  });
  const [editing, setEditing] = useState<{ block: RecordBlock; index: number; isNew: boolean }>();
  const [confirming, setConfirming] = useState<'publish' | 'discard' | 'archive'>();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const drag = useRef<{
    from: number;
    to: number;
    startY: number;
    scrollY: number;
    rects: DOMRect[];
  } | null>(null);
  const [dragVisual, setDragVisual] = useState<{
    from: number;
    to: number;
    offset: number;
    height: number;
  }>();
  const [preview, setPreview] = useState<RecordAnswers>({});

  async function change(
    action: () => Promise<RecordTemplateDetail>,
    message: string | null,
  ): Promise<boolean> {
    setBusy(true);
    setError(undefined);
    try {
      queryClient.setQueryData(detailKey(templateId), await action());
      await queryClient.invalidateQueries({ queryKey: listKey });
      await queryClient.invalidateQueries({ queryKey: ['company', 'records', 'themes'] });
      if (message) snackbar.success(message);
      return true;
    } catch (failure) {
      const issue = getApiIssues(failure)[0];
      setError(issue ? issue.message : getApiError(failure));
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (detail.isLoading) return <LoadingCard label="Carregando modelo" />;
  if (detail.error || !detail.data) return <ErrorCard message={getApiError(detail.error)} />;
  const template = detail.data;
  const blocks = template.blocks;
  const published = new Map(template.publishedBlocks.map((block) => [block.id, canonical(block)]));
  const next = template.publishedVersion + 1;
  const saveBlocks = (nextBlocks: RecordBlock[]) =>
    change(() => recordsApi.saveDraft(template.id, nextBlocks), null);
  const move = (index: number, delta: number) => {
    const nextBlocks = [...blocks];
    const [item] = nextBlocks.splice(index, 1);
    nextBlocks.splice(index + delta, 0, item!);
    void saveBlocks(nextBlocks);
  };
  const editorFor = (index: number) =>
    editing && editing.index === index ? (
      <BlockEditor
        block={editing.block}
        isNew={editing.isNew}
        themes={themes}
        onOpenThemes={onOpenThemes}
        onCancel={() => setEditing(undefined)}
        onApply={(block) => {
          const nextBlocks = editing.isNew
            ? [...blocks, block]
            : blocks.map((item, position) => (position === index ? block : item));
          void saveBlocks(nextBlocks).then((saved) => {
            if (saved) setEditing(undefined);
          });
        }}
      />
    ) : null;

  return (
    <div className="grid min-w-0 gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="grid gap-1">
          <h2 className="text-xl font-bold tracking-[-.02em]">{template.displayName}</h2>
          <p className="text-xs text-content-muted">
            {template.publishedAt
              ? `v${String(template.publishedVersion)} publicada por ${template.publishedByDisplayName ?? ''} em ${formatDate(template.publishedAt, timeZone)}`
              : 'Ainda não publicado: não aparece para quem atende.'}{' '}
            · vale para {servicesLabel(template, services)}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => setSettingsOpen(true)}>
            <PencilLine className="size-4" />
            Ajustes do modelo
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirming('archive')}>
            {template.active ? 'Arquivar' : 'Reativar'}
          </Button>
        </div>
      </div>

      <SegmentedControl
        tabs
        compact
        className="w-full"
        label="Modo"
        value={mode}
        onChange={(nextMode) => onMode(nextMode)}
        options={[
          { value: 'edit', label: 'Montar' },
          { value: 'preview', label: 'Pré-visualizar' },
        ]}
      />

      {template.hasDraft ? (
        <Notice tone="warning">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>
              <strong>Rascunho em montagem.</strong>{' '}
              {template.publishedVersion
                ? `Quem atende continua usando a v${String(template.publishedVersion)}.`
                : 'O modelo ainda não está disponível.'}
            </span>
            <span className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setConfirming('discard')}>
                Descartar
              </Button>
              <Button size="sm" onClick={() => setConfirming('publish')}>
                Publicar v{next}
              </Button>
            </span>
          </div>
        </Notice>
      ) : null}
      {error && !confirming ? <RequestAlert message={error} /> : null}

      {mode === 'preview' ? (
        <div className="grid gap-3 rounded-2xl border border-surface-border bg-surface-card p-5 shadow-soft">
          <ContentCardHeader
            title="Pré-visualização da ficha"
            icon={<CardIconEye className="size-4" />}
          />
          <p className="text-xs text-content-muted">
            Pré-visualização do {template.hasDraft ? 'rascunho' : 'modelo em uso'}, como quem atende
            vê. Nada é gravado.
          </p>
          {blocks.length ? (
            <RecordForm blocks={blocks} answers={preview} onChange={setPreview} today={today} />
          ) : (
            <p className="text-sm text-content-faint">Nenhum bloco ainda.</p>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-surface-border bg-surface-card shadow-soft">
          {blocks.length ? (
            <ol className="divide-y divide-surface-border">
              {blocks.map((block, index) => {
                const state = !published.has(block.id)
                  ? 'nova'
                  : published.get(block.id) !== canonical(block)
                    ? 'alterada'
                    : null;
                return (
                  <li
                    key={block.id}
                    data-block-index={index}
                    style={
                      dragVisual
                        ? {
                            transform: `translateY(${
                              index === dragVisual.from
                                ? dragVisual.offset
                                : dragVisual.from < index && index <= dragVisual.to
                                  ? -dragVisual.height
                                  : dragVisual.to <= index && index < dragVisual.from
                                    ? dragVisual.height
                                    : 0
                            }px)`,
                          }
                        : undefined
                    }
                    className={cn(
                      'relative bg-surface-card',
                      dragVisual?.from === index
                        ? 'z-10 shadow-xl ring-2 ring-brand-500'
                        : 'transition-transform duration-150 motion-reduce:transition-none',
                    )}
                  >
                    <div
                      className={cn(
                        'flex flex-wrap items-center gap-3 px-4 py-3',
                        state && 'bg-info/5',
                      )}
                    >
                      <div className="flex shrink-0 items-center gap-1">
                        <IconButton
                          label={`Arrastar ${blockKinds[block.kind].toLowerCase()} ${String(index + 1)}`}
                          aria-description="Arraste para reordenar ou use as teclas de seta para cima e para baixo."
                          icon={<GripVertical className="size-4" />}
                          className="touch-none select-none cursor-grab active:cursor-grabbing"
                          disabled={busy || !!editing || blocks.length < 2}
                          onPointerDown={(event) => {
                            if (event.button !== 0) return;
                            event.preventDefault();
                            event.currentTarget.focus();
                            event.currentTarget.setPointerCapture?.(event.pointerId);
                            const rects = Array.from(
                              event.currentTarget
                                .closest('ol')!
                                .querySelectorAll<HTMLElement>('[data-block-index]'),
                              (row) => row.getBoundingClientRect(),
                            );
                            drag.current = {
                              from: index,
                              to: index,
                              startY: event.clientY,
                              scrollY: window.scrollY,
                              rects,
                            };
                            setDragVisual({
                              from: index,
                              to: index,
                              offset: 0,
                              height: rects[index]!.height,
                            });
                          }}
                          onPointerMove={(event) => {
                            if (!drag.current) return;
                            const current = drag.current;
                            const offset =
                              event.clientY - current.startY + window.scrollY - current.scrollY;
                            const center =
                              current.rects[current.from]!.top +
                              current.rects[current.from]!.height / 2 +
                              offset;
                            let to = 0;
                            current.rects.forEach((rect, position) => {
                              if (position !== current.from && center >= rect.top + rect.height / 2)
                                to++;
                            });
                            current.to = to;
                            setDragVisual({
                              from: current.from,
                              to,
                              offset,
                              height: current.rects[current.from]!.height,
                            });
                            if (event.clientY < 80) window.scrollBy({ top: -16 });
                            else if (event.clientY > window.innerHeight - 80)
                              window.scrollBy({ top: 16 });
                          }}
                          onPointerUp={() => {
                            const current = drag.current;
                            drag.current = null;
                            setDragVisual(undefined);
                            if (current && current.from !== current.to)
                              move(current.from, current.to - current.from);
                          }}
                          onPointerCancel={() => {
                            drag.current = null;
                            setDragVisual(undefined);
                          }}
                          onLostPointerCapture={() => {
                            drag.current = null;
                            setDragVisual(undefined);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                              event.preventDefault();
                              const delta = event.key === 'ArrowUp' ? -1 : 1;
                              if (
                                !drag.current &&
                                index + delta >= 0 &&
                                index + delta < blocks.length
                              )
                                move(index, delta);
                            }
                            if (event.key === 'Escape') {
                              drag.current = null;
                              setDragVisual(undefined);
                            }
                          }}
                        />
                      </div>
                      <BlockSummary block={block} themes={themes} state={state} />
                      <div className="flex shrink-0 gap-1">
                        <IconButton
                          label={`Editar ${blockKinds[block.kind].toLowerCase()} ${String(index + 1)}`}
                          icon={<PencilLine className="size-4" />}
                          onClick={() => setEditing({ block, index, isNew: false })}
                        />
                        <IconButton
                          tone="danger"
                          label={`Remover ${blockKinds[block.kind].toLowerCase()} ${String(index + 1)}`}
                          icon={<Trash2 className="size-4" />}
                          disabled={busy}
                          onClick={() =>
                            void saveBlocks(blocks.filter((_, position) => position !== index))
                          }
                        />
                      </div>
                    </div>
                    {editorFor(index)}
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="px-5 py-5 text-sm text-content-muted">
              Nenhum bloco ainda. Comece por um título e pelas perguntas.
            </p>
          )}
          {editorFor(blocks.length)}
          <div className="flex flex-wrap items-center gap-2 border-t border-surface-border px-4 py-3">
            <span className="text-xs text-content-muted">Adicionar:</span>
            {(['HEADING', 'SUBHEADING', 'DESCRIPTION', 'QUESTION'] as const).map((kind) => (
              <Button
                key={kind}
                size="sm"
                variant="outline"
                onClick={() =>
                  setEditing({ block: newBlock(kind), index: blocks.length, isNew: true })
                }
              >
                <Plus className="size-4" />
                {blockKinds[kind]}
              </Button>
            ))}
          </div>
        </div>
      )}

      {template.versions.length ? (
        <div className="grid gap-2 rounded-2xl border border-surface-border bg-surface-card p-5 shadow-soft">
          <ContentCardHeader
            title="Versões publicadas"
            level={3}
            icon={<CardIconHistory className="size-4" />}
          />
          {[...template.versions].reverse().map((version) => (
            <div key={version.version} className="flex flex-wrap justify-between gap-2 text-sm">
              <span>
                <strong>v{version.version}</strong> · {version.questionCount}{' '}
                {version.questionCount === 1 ? 'pergunta' : 'perguntas'}
              </span>
              <span className="text-xs text-content-muted">
                {version.publishedByDisplayName} · {formatDate(version.publishedAt, timeZone)}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {settingsOpen ? (
        <SettingsDialog
          template={template}
          services={services}
          onClose={() => setSettingsOpen(false)}
          onCreated={() => undefined}
        />
      ) : null}
      {confirming ? (
        <Modal
          open
          onOpenChange={() => setConfirming(undefined)}
          title={
            confirming === 'publish'
              ? `Publicar a versão ${String(next)}`
              : confirming === 'discard'
                ? 'Descartar o rascunho'
                : template.active
                  ? 'Arquivar o modelo'
                  : 'Reativar o modelo'
          }
          description={
            confirming === 'publish'
              ? 'Quem atende passa a preencher com estas perguntas. Fichas gravadas não mudam: continuam mostrando as perguntas da versão que responderam.'
              : confirming === 'discard'
                ? 'O modelo volta a ser a versão em uso. O que foi montado no rascunho se perde.'
                : template.active
                  ? 'O modelo deixa de aparecer para preencher ficha nova. As fichas já gravadas continuam no prontuário.'
                  : 'O modelo volta a aparecer para preencher.'
          }
          footer={
            <>
              <Button variant="outline" onClick={() => setConfirming(undefined)}>
                Voltar
              </Button>
              <Button
                loading={busy}
                onClick={() => {
                  const run =
                    confirming === 'publish'
                      ? change(
                          () => recordsApi.publish(template.id),
                          `Versão ${String(next)} publicada.`,
                        )
                      : confirming === 'discard'
                        ? change(() => recordsApi.discardDraft(template.id), 'Rascunho descartado.')
                        : change(
                            () => recordsApi.setTemplateActive(template.id, !template.active),
                            template.active ? 'Modelo arquivado.' : 'Modelo reativado.',
                          );
                  void run.then((done) => {
                    if (done) setConfirming(undefined);
                  });
                }}
              >
                {confirming === 'publish'
                  ? 'Publicar'
                  : confirming === 'discard'
                    ? 'Descartar'
                    : template.active
                      ? 'Arquivar'
                      : 'Reativar'}
              </Button>
            </>
          }
        >
          <p className="text-sm">
            {confirming === 'publish'
              ? `${String(questionsOf(blocks).length)} ${questionsOf(blocks).length === 1 ? 'pergunta' : 'perguntas'} no modelo.`
              : template.displayName}
          </p>
          {error ? <RequestAlert message={error} /> : null}
        </Modal>
      ) : null}
    </div>
  );
}

function BlockSummary({
  block,
  themes,
  state,
}: {
  block: RecordBlock;
  themes: readonly HealthTheme[];
  state: 'nova' | 'alterada' | null;
}) {
  return (
    <div className="grid min-w-0 flex-1 gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-content-faint">
        {blockKinds[block.kind]}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {block.kind === 'QUESTION' ? (
          <>
            <strong className="text-sm">{block.label}</strong>
            {block.required ? (
              <StatusText tone="brand" className="text-xs">
                Obrigatória
              </StatusText>
            ) : null}
            {block.themeKey ? <ThemeTag theme={themeOf(themes, block.themeKey)} /> : null}
          </>
        ) : block.kind === 'DESCRIPTION' ? (
          <span className="line-clamp-2 text-sm text-content-muted">
            {richTextToPlain(block.html)}
          </span>
        ) : (
          <strong className={block.kind === 'HEADING' ? 'text-base' : 'text-sm'}>
            {block.text}
          </strong>
        )}
        {state ? (
          <StatusText tone="progress" className="text-xs">
            {state}
          </StatusText>
        ) : null}
      </div>
      {block.kind === 'QUESTION' ? (
        <span className="text-xs text-content-muted">
          {questionTypes[block.type].label}
          {block.options.length
            ? ` · ${block.options.map((option) => option.label).join(', ')}`
            : ''}
          {block.help ? ' · com texto explicativo' : ''}
        </span>
      ) : null}
    </div>
  );
}

/// Nome, serviços e assinatura do modelo — valem na hora, sem versão. É o mesmo diálogo para criar.
function SettingsDialog({
  template,
  services,
  onClose,
  onCreated,
}: {
  template?: RecordTemplateDetail;
  services: RecordServiceOptions | undefined;
  onClose: () => void;
  onCreated: (template: RecordTemplateDetail) => void;
}) {
  const formId = useId();
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [settings, setSettings] = useState<RecordTemplateSettings>({
    displayName: template?.displayName ?? '',
    allServices: template?.allServices ?? true,
    serviceIds: template?.serviceIds ?? [],
    signaturePolicy: template?.signaturePolicy ?? 'OPTIONAL',
    kind: template?.kind ?? 'RECORD',
  });
  const isCheck = settings.kind === 'CHECK';
  const [nameError, setNameError] = useState<string>();
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const available = services?.available ?? false;
  const offered = (services?.services ?? []).filter(
    (service) => service.active || settings.serviceIds.includes(service.id),
  );

  async function submit() {
    setError(undefined);
    const name = settings.displayName.trim();
    if (name.length < 2 || name.length > 120) {
      setNameError('Use de 2 a 120 caracteres no nome do modelo.');
      return;
    }
    setSaving(true);
    try {
      const input = { ...settings, displayName: name };
      const saved = template
        ? await recordsApi.updateTemplate(template.id, input)
        : await recordsApi.createTemplate(input);
      queryClient.setQueryData(detailKey(saved.id), saved);
      await queryClient.invalidateQueries({ queryKey: listKey });
      snackbar.success(template ? 'Modelo atualizado.' : 'Modelo criado.');
      if (template) onClose();
      else onCreated(saved);
    } catch (failure) {
      setError(getApiError(failure));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(value) => {
        if (!value) onClose();
      }}
      title={template ? 'Ajustes do modelo' : 'Novo modelo de ficha'}
      description="Valem na hora e não geram versão."
      footer={
        <>
          <FormErrorSummary form={formId} className="mr-auto" />
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" form={formId} loading={saving}>
            {template ? 'Salvar' : 'Criar modelo'}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        noValidate
        className="grid gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {template ? (
          <p className="text-sm text-content-muted">
            Tipo:{' '}
            <strong className="text-content">
              {isCheck ? 'Checagem pré-sessão' : 'Ficha do cliente'}
            </strong>
            . O tipo não muda depois de criado.
          </p>
        ) : (
          <KindChoice
            value={settings.kind ?? 'RECORD'}
            onChange={(kind) => setSettings({ ...settings, kind })}
          />
        )}
        <Input
          label="Nome do modelo"
          value={settings.displayName}
          error={nameError}
          placeholder={isCheck ? 'Ex.: Checagem pré-sessão' : 'Ex.: Saúde geral, Coloração'}
          onChange={(event) => {
            setSettings({ ...settings, displayName: event.target.value });
            setNameError(undefined);
          }}
        />
        {available ? (
          <div className="grid gap-2">
            <Toggle
              checked={settings.allServices}
              onChange={(allServices) => setSettings({ ...settings, allServices })}
            >
              Vale para todos os serviços
            </Toggle>
            {settings.allServices ? null : (
              <ToggleChips
                label="Serviços do modelo"
                itemNoun="serviços"
                selected={settings.serviceIds}
                options={offered.map((service) => ({
                  value: service.id,
                  label: service.displayName,
                }))}
                onChange={(serviceIds) => setSettings({ ...settings, serviceIds })}
              />
            )}
            <p className="text-xs text-content-muted">
              {isCheck
                ? 'A checagem aparece no atendimento desses serviços. Se mais de uma valer, quem atende escolhe.'
                : 'A ficha aparece para o cliente que fez ou tem marcado um desses serviços. Qualquer modelo ainda pode ser preenchido à mão.'}
            </p>
          </div>
        ) : (
          <p className="text-xs text-content-muted">
            Sem o módulo Serviços, todo modelo vale para todos os serviços.
          </p>
        )}
        {isCheck ? (
          <p className="text-xs text-content-muted">
            A checagem é respondida a cada atendimento e não é assinada.
          </p>
        ) : (
          <div className="grid gap-2">
            <span className="text-sm font-medium text-content-secondary">Assinatura</span>
            <ChoiceChips
              label="Assinatura"
              value={settings.signaturePolicy}
              options={[
                { value: 'OPTIONAL', label: 'Opcional' },
                { value: 'EVERY_VERSION', label: 'Pedir em toda versão' },
              ]}
              onChange={(value) =>
                setSettings({
                  ...settings,
                  signaturePolicy: value as RecordTemplateSettings['signaturePolicy'],
                })
              }
            />
            <p className="text-xs text-content-muted">
              “Pedir” marca a versão como pendente até alguém assinar na tela ou anexar a via
              assinada. Não trava nada.
            </p>
          </div>
        )}
        {error ? <RequestAlert message={error} /> : null}
      </form>
    </Modal>
  );
}

/// O tipo do modelo, escolhido ao criar (ADR 0038, adendo de 2026-09-28): a ficha do cliente ou a
/// checagem respondida a cada atendimento.
function KindChoice({
  value,
  onChange,
}: {
  value: RecordTemplateKind;
  onChange: (kind: RecordTemplateKind) => void;
}) {
  const options: { kind: RecordTemplateKind; title: string; hint: string }[] = [
    {
      kind: 'RECORD',
      title: 'Ficha do cliente',
      hint: 'Preenchida uma vez e atualizada quando algo muda. Versionada, assinável, entra no Quadro de saúde e no PDF.',
    },
    {
      kind: 'CHECK',
      title: 'Checagem pré-sessão',
      hint: 'Respondida a cada atendimento, na página do atendimento. Fica na sessão; não entra no PDF nem pede assinatura.',
    },
  ];
  return (
    <div className="grid gap-2">
      <span className="text-sm font-medium text-content-secondary">Tipo</span>
      <div role="radiogroup" aria-label="Tipo do modelo" className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => (
          <button
            key={option.kind}
            type="button"
            role="radio"
            aria-checked={value === option.kind}
            onClick={() => onChange(option.kind)}
            className={cn(
              'grid cursor-pointer gap-1 rounded-xl border p-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50',
              value === option.kind
                ? 'border-brand-500 bg-brand-500/10'
                : 'border-surface-border hover:border-brand-500/50',
            )}
          >
            <span className="text-sm font-semibold">{option.title}</span>
            <span className="text-xs text-content-muted">{option.hint}</span>
          </button>
        ))}
      </div>
      <p className="text-xs text-content-muted">O tipo não muda depois de criado.</p>
    </div>
  );
}
